/**
 * 创建/编辑分类弹窗
 */

import { useState, useEffect, useRef } from 'react'
import { Loader2, Upload, ImageIcon } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { useToast } from '@/shared/hooks/useToast'
import { CategoryApiService, AppApiService, uploadIconToS3 } from '../services/app-api'
import type { Category, CreateCategoryRequest, UpdateCategoryRequest } from '../types/app'

interface CategoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  appId: string
  category: Category | null // null 表示创建，非 null 表示编辑
  onSuccess: (category: Category) => void
}

export default function CategoryDialog({
  open,
  onOpenChange,
  appId,
  category,
  onSuccess,
}: CategoryDialogProps) {
  const { showSuccess, showError } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isEditing = !!category

  // 表单状态
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    description: '',
  })
  const [iconUrl, setIconUrl] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploadingIcon, setIsUploadingIcon] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // 初始化表单数据
  useEffect(() => {
    if (category) {
      setFormData({
        category_id: category.category_id,
        name: category.name,
        description: category.description || '',
      })
      setIconUrl(category.icon_url || null)
    } else {
      setFormData({
        category_id: '',
        name: '',
        description: '',
      })
      setIconUrl(null)
    }
    setErrors({})
  }, [category, open])

  // 验证表单
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!isEditing && !formData.category_id.trim()) {
      newErrors.category_id = '分类 ID 不能为空'
    } else if (!isEditing && !/^[a-z][a-z0-9_]*$/.test(formData.category_id)) {
      newErrors.category_id = '分类 ID 必须以小写字母开头，只能包含小写字母、数字和下划线'
    }

    if (!formData.name.trim()) {
      newErrors.name = '分类名称不能为空'
    } else if (formData.name.length > 50) {
      newErrors.name = '分类名称不能超过 50 个字符'
    }

    if (formData.description.length > 200) {
      newErrors.description = '分类描述不能超过 200 个字符'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 提交表单
  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      if (isEditing) {
        // 编辑分类
        const updateData: UpdateCategoryRequest = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          icon_url: iconUrl || undefined,
        }

        const response = await CategoryApiService.updateCategory(
          appId,
          category.category_id,
          updateData
        )

        if (response.success && response.data) {
          showSuccess('分类已更新')
          onSuccess(response.data)
        }
      } else {
        // 创建分类
        const createData: CreateCategoryRequest = {
          category_id: formData.category_id.trim(),
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          icon_url: iconUrl || undefined,
        }

        const response = await CategoryApiService.createCategory(appId, createData)

        if (response.success && response.data) {
          showSuccess('分类已创建')
          onSuccess(response.data)
        }
      }
    } catch (error: any) {
      console.error('❌ [CategoryDialog] 保存失败:', error)
      if (error.response?.data?.error?.code === 'CATEGORY_EXISTS') {
        setErrors({ category_id: '分类 ID 已存在' })
      } else {
        showError(isEditing ? '更新分类失败' : '创建分类失败')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // 上传图标
  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // 验证文件类型
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
    if (!validTypes.includes(file.type)) {
      showError('只支持 PNG、JPG、WEBP 格式的图片')
      return
    }

    // 验证文件大小（最大 1MB）
    if (file.size > 1024 * 1024) {
      showError('图片大小不能超过 1MB')
      return
    }

    // 创建模式下，需要先有 category_id 才能上传图标
    if (!isEditing && !formData.category_id.trim()) {
      showError('请先填写分类 ID')
      return
    }

    setIsUploadingIcon(true)
    try {
      const categoryId = isEditing ? category.category_id : formData.category_id.trim()
      const fileType = file.type.split('/')[1] as 'png' | 'jpg' | 'jpeg' | 'webp'

      // 获取上传 URL
      const uploadResponse = await AppApiService.getCategoryIconUploadUrl(
        appId,
        categoryId,
        {
          file_type: fileType,
          file_size: file.size,
        }
      )

      if (uploadResponse.success && uploadResponse.data) {
        // 上传到 S3
        await uploadIconToS3(uploadResponse.data.upload_url, file)
        setIconUrl(uploadResponse.data.icon_url)
        showSuccess('图标已上传')
      }
    } catch (error) {
      console.error('❌ [CategoryDialog] 上传图标失败:', error)
      showError('上传图标失败')
    } finally {
      setIsUploadingIcon(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? '编辑分类' : '创建分类'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? '修改分类信息'
              : '创建新的项目分类，方便用户浏览和发现项目。'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* 图标上传 */}
          <div className="flex items-center gap-4">
            <div className="relative">
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt="分类图标"
                  className="h-16 w-16 rounded-lg object-cover shadow-sm"
                />
              ) : (
                <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center shadow-sm">
                  <ImageIcon className="h-8 w-8 text-primary" />
                </div>
              )}
              {isUploadingIcon && (
                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleIconUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingIcon || (!isEditing && !formData.category_id.trim())}
              >
                <Upload className="h-4 w-4 mr-2" />
                {isUploadingIcon ? '上传中...' : iconUrl ? '更换图标' : '上传图标'}
              </Button>
              <p className="text-xs text-slate-500">
                {!isEditing && !formData.category_id.trim()
                  ? '请先填写分类 ID'
                  : '支持 PNG、JPG、WEBP，最大 1MB'}
              </p>
            </div>
          </div>

          {/* 分类 ID（仅创建时显示） */}
          {!isEditing && (
            <div className="grid gap-2">
              <Label htmlFor="category_id">
                分类 ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="category_id"
                placeholder="例如：work、companion"
                value={formData.category_id}
                onChange={(e) =>
                  setFormData({ ...formData, category_id: e.target.value.toLowerCase() })
                }
                className={errors.category_id ? 'border-red-500' : ''}
              />
              {errors.category_id ? (
                <p className="text-xs text-red-500">{errors.category_id}</p>
              ) : (
                <p className="text-xs text-slate-500">
                  小写字母开头，只能包含小写字母、数字和下划线
                </p>
              )}
            </div>
          )}

          {/* 分类名称 */}
          <div className="grid gap-2">
            <Label htmlFor="name">
              分类名称 <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder="例如：工作效率"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
          </div>

          {/* 分类描述 */}
          <div className="grid gap-2">
            <Label htmlFor="description">分类描述</Label>
            <Textarea
              id="description"
              placeholder="简要描述这个分类..."
              className="resize-none"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            {errors.description ? (
              <p className="text-xs text-red-500">{errors.description}</p>
            ) : (
              <p className="text-xs text-slate-500">
                {formData.description.length}/200
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? '保存中...' : '创建中...'}
              </>
            ) : isEditing ? (
              '保存'
            ) : (
              '创建'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
